import { useEffect, useState } from 'react';
import { Award, Clock, PackageCheck, Star, AlertTriangle, Lightbulb, Loader2 } from 'lucide-react';
import { round2 } from '../lib/api';

const GRADE_COLORS = { 'A+': '#1B5E20', A: '#2E7D32', B: '#E67E22', C: '#D35400', D: '#B71C1C' };

export function GradeBadge({ grade, score, size = 'md' }) {
  const big = size === 'lg';
  return (
    <span data-testid="scorecard-grade" className={`inline-grid place-items-center rounded-2xl text-white font-heading font-black ${big ? 'w-20 h-20 text-3xl' : 'w-11 h-11 text-base'}`}
      style={{ background: GRADE_COLORS[grade] || '#555' }} title={`Score ${score}/100`}>
      {grade}
    </span>
  );
}

function Metric({ Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-border bg-white p-3">
      <div className="text-xs text-ink/60 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5"/>{label}</div>
      <div data-testid={testid} className="font-heading font-bold text-xl mt-1">{value}</div>
    </div>
  );
}

export function ScorecardCard({ sc }) {
  const m = sc.metrics;
  return (
    <div data-testid={`scorecard-${sc.store_id}`} className="card-soft p-5 space-y-4">
      <div className="flex items-center gap-4">
        <GradeBadge grade={sc.grade} score={sc.score} size="lg"/>
        <div>
          <div className="font-heading font-bold text-lg">{sc.store_name}</div>
          <div data-testid="scorecard-score" className="text-sm text-ink/70">{sc.score}/100 · {sc.label}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric Icon={Star} label="Rating" value={m.rating_avg ? `${m.rating_avg} ★` : '—'} testid="scorecard-rating"/>
        <Metric Icon={PackageCheck} label="Orders completed" value={`${m.delivered_pct}%`} testid="scorecard-delivered"/>
        <Metric Icon={Clock} label="Delivered within a day" value={m.avg_delivery_hours == null ? '—' : `${m.on_time_pct}%`} testid="scorecard-ontime"/>
        <Metric Icon={AlertTriangle} label="Complaints" value={m.complaints} testid="scorecard-complaints"/>
      </div>
      {sc.tips && (
        <div className="rounded-xl bg-[#FFF8EE] border border-[#E67E22]/30 p-3 space-y-1">
          <div className="text-xs font-bold uppercase tracking-wider text-[#D35400] flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5"/>How to improve</div>
          {sc.tips.map(t => <div key={t} className="text-sm text-ink/80">• {t}</div>)}
        </div>
      )}
    </div>
  );
}

export default function SellerScorecard() {
  const [items, setItems] = useState(null);
  useEffect(() => { round2.ownerScorecard().then(d => setItems(d.items)).catch(() => setItems([])); }, []);
  if (items === null) return <Loader2 className="w-6 h-6 animate-spin text-[#E67E22] mt-6"/>;
  return (
    <section data-testid="seller-scorecard" className="mt-5 space-y-4">
      <div className="flex items-center gap-2 font-heading font-bold text-xl"><Award className="w-5 h-5 text-[#E67E22]"/>Your Seller Scorecard</div>
      <p className="text-sm text-ink/60">Customers see your grade on your shop page. A higher grade means more orders.</p>
      {items.length === 0 ? <p className="text-sm text-ink/60">Add a shop to see your score.</p> : items.map(sc => <ScorecardCard key={sc.store_id} sc={sc}/>)}
    </section>
  );
}
